import { HttpErrorResponse } from '@angular/common/http'
import { Injectable } from '@angular/core'
import { TranslateService } from '@ngx-translate/core'
import { BillingService } from '@services/billing/billing.service'
import {
  AcceptableMeasurementTypes,
  AggregatedMetric,
  AggregationMethods,
  AlertRule,
  BaseUnitID,
  BaseUnitTypeID,
  BaseUnitWithInfo,
  Calculation,
  ChangeEvent,
  CompositeUnitType,
  ConsumerWidgetConfig,
  Cost,
  CostsTypes,
  CreateDeviceMapping,
  DMSDeviceMapping,
  DashboardConfig,
  DataRepresentations,
  DateRangeComparisonWidgetConfig,
  EmmaAlertMessage,
  EmmaAlertMessagesOptions,
  FormulaNodeTypes,
  GroupByUnits,
  MeteringPoint,
  Metric,
  QueryableTypedID,
  Rate,
  ReplaceDeviceMapping,
  TypedQueryableID,
  Unit,
  UnitPrefix,
  UnitTypeDefaults,
  WageTypeInfo,
} from '@services/proficloud.interfaces'
import { ProficloudService } from '@services/proficloud.service'
import { BehaviorSubject, Observable, Subscription, forkJoin, tap } from 'rxjs'
import { EmmaHttpService } from '../backend/emma-http.service'

@Injectable({
  providedIn: 'root',
})
export class EmmaStore {
  // Internal error flag
  private errored = false

  // Note: This is for externally flagging that an error has occurred on a data fetch
  // This is necessary because it could happen at any time and we need to know in components when we should not clear error status messages
  public dataFetchError = false

  // Private subjects
  private _dashboards$: BehaviorSubject<DashboardConfig[]> = new BehaviorSubject([])

  private _allTypesAndUnits$: BehaviorSubject<{
    baseUnitTypeDefaults: Map<BaseUnitTypeID, UnitTypeDefaults>
    wageTypeInfo: Map<Exclude<AcceptableMeasurementTypes, null>, WageTypeInfo>
    baseTypes: BaseUnitTypeID[]
    compositeTypes: CompositeUnitType[]
    baseUnits: BaseUnitWithInfo[]
  }> = new BehaviorSubject({
    baseUnitTypeDefaults: new Map(),
    wageTypeInfo: new Map(),
    baseTypes: [],
    compositeTypes: [],
    baseUnits: [],
  })

  private _queryableDataSources$: BehaviorSubject<{
    tree: MeteringPoint[]
    inbox: Metric[]
    sums: Calculation[]
    avgs: Calculation[]
    kpis: Calculation[]
    mappings: DMSDeviceMapping[]
  }> = new BehaviorSubject({
    tree: [],
    inbox: [],
    sums: [],
    avgs: [],
    kpis: [],
    mappings: [],
  })

  private _alertMessagesAndRules$: BehaviorSubject<{
    rules: AlertRule[]
    messages: EmmaAlertMessage[]
  }> = new BehaviorSubject({
    rules: [],
    messages: [],
  })

  private _rawDataChanged$: BehaviorSubject<{
    metridId: string
  }> = new BehaviorSubject({ metridId: '' })

  private _alertRules$: BehaviorSubject<AlertRule[]> = new BehaviorSubject([])

  private _alertMessages$: BehaviorSubject<EmmaAlertMessage | EmmaAlertMessage[]> = new BehaviorSubject([])

  private _alertMessageStreamSubscription: Subscription

  private _costs$: BehaviorSubject<Cost[]> = new BehaviorSubject([])

  // Public subjects
  public readonly dashboards$: Observable<DashboardConfig[]> = this._dashboards$.asObservable()

  public readonly queryableDataSources$: Observable<{
    tree: MeteringPoint[]
    inbox: Metric[]
    sums: Calculation[]
    avgs: Calculation[]
    kpis: Calculation[]
    mappings: DMSDeviceMapping[]
  }> = this._queryableDataSources$.asObservable()

  public readonly allTypesAndUnits$: Observable<{
    baseUnitTypeDefaults: Map<BaseUnitTypeID, UnitTypeDefaults>
    wageTypeInfo: Map<Exclude<AcceptableMeasurementTypes, null>, WageTypeInfo>
    baseTypes: BaseUnitTypeID[]
    compositeTypes: CompositeUnitType[]
    baseUnits: BaseUnitWithInfo[]
  }> = this._allTypesAndUnits$.asObservable()

  public readonly alertMessagesAndRules$: Observable<{
    rules: AlertRule[]
    messages: EmmaAlertMessage[]
  }> = this._alertMessagesAndRules$.asObservable()

  public readonly alertRules$: Observable<AlertRule[]> = this._alertRules$.asObservable()

  public readonly alertMessages$: Observable<EmmaAlertMessage | EmmaAlertMessage[]> = this._alertMessages$.asObservable()

  public readonly costs$: Observable<Cost[]> = this._costs$.asObservable()

  public readonly rawDataChanged$ = this._rawDataChanged$.asObservable()

  // Note: This is different to the MemberStore pattern, as I need a way for components to know if the store has been completely initialized
  // Feels like a break of the pattern but otherwise don't know how to deal with a component that needs to wait for multiple resources to have all been loaded
  public storeInitialised$ = new BehaviorSubject<boolean>(true)
  private dashboardsInitialised = false
  private queryableDataSourcesInitialised = false
  private allTypesAndUnitsInitialised = false
  private costsInitialised = false
  private alertsInitialised = false

  private organisationSwitchBegunSubscription: Subscription
  private organisationsListedSubscription: Subscription
  private organisationSwitchedSubscription: Subscription
  private emmaSubscriptionsListedSubscription: Subscription

  constructor(
    private billing: BillingService,
    private emmaHttp: EmmaHttpService,
    private proficloud: ProficloudService,
    private translate: TranslateService
  ) {
    // Subscribe to relevant observables
    this.organisationSwitchBegunSubscription = this.proficloud.organisationSwitchBegun$.subscribe(() => {
      this.reset()
    })

    this.organisationsListedSubscription = this.proficloud.organisationsListed$.subscribe(() => {
      // TODO: Anything?
    })

    // Note: The reason we don't do anything here is because that EMMA subscriptions always get loaded after the organisation is switched
    // Doesn't feel good, but we always have to wait for the subscriptions before anything else can happen.
    // Perhaps this could be solved by pulling the subscriptions into the store, even though it's a billing service?
    this.organisationSwitchedSubscription = this.proficloud.organisationSwitched$.subscribe(() => {
      // TODO: Anything?
    })

    this.emmaSubscriptionsListedSubscription = this.billing.emmaSubscriptionsListed$.subscribe((success) => {
      this.reset()
      this.refreshQueryableDataSources()
      this.loadAllUnitsAndMeasurementTypes()
      this.loadDashboardsData()
      this.getCosts()

      // Alerting
      this.loadAlertMessagesAndRules()
      this.setupEmmaAlerts()
    })
  }

  private reset() {
    this.dashboardsInitialised = false
    this.queryableDataSourcesInitialised = false
    this.allTypesAndUnitsInitialised = false
    this.costsInitialised = false
    this.alertsInitialised = false
  }

  private emitStoreInitialised() {
    if (
      this.dashboardsInitialised &&
      this.queryableDataSourcesInitialised &&
      this.allTypesAndUnitsInitialised &&
      this.costsInitialised &&
      this.alertsInitialised
    ) {
      this.storeInitialised$.next(this.errored)
    }
  }

  private loadDashboardsData() {
    this.getUserDashboards().subscribe({
      next: (res) => {
        let dashboards: DashboardConfig[]
        if (res.dashboard_configuration.length) {
          const parsed = JSON.parse(res.dashboard_configuration)
          dashboards = Array.isArray(parsed) ? (parsed as DashboardConfig[]) : [parsed as DashboardConfig]
          if (!dashboards.length) {
            dashboards = [
              {
                version: 2,
                title: this.translate.instant('EMMA_SERVICE.NEW_DASHBOARD'),
                description: '',
                widgets: [],
              },
            ]
          }
          // Update widget data source names, as they might have been changed
          dashboards.forEach((db) => {
            // Manually create correctly typed objects for each widget (feels bad but i don't see how else to do it)
            for (let i = 0; i < db.widgets.length; i++) {
              db.version = 2
              // Construct properly typed configs
              if (['consumer', 'rankedConsumer', 'rankedTimePeriod'].includes(db.widgets[i].comparisonType)) {
                db.widgets[i] = ConsumerWidgetConfig.buildFromObject(db.widgets[i])
              }
              if (db.widgets[i].comparisonType === 'timePeriod') {
                db.widgets[i] = DateRangeComparisonWidgetConfig.buildFromObject(db.widgets[i])
              }
            }
          })
        } else {
          // Create an empty dashboard
          dashboards = [
            {
              version: 2,
              title: this.translate.instant('EMMA_SERVICE.NEW_DASHBOARD'),
              description: '',
              widgets: [],
            },
          ]
        }

        this._dashboards$.next(dashboards)
        this.dashboardsInitialised = true
        this.emitStoreInitialised()
      },
      error: (err: HttpErrorResponse) => {
        this.errored = true
        this._dashboards$.error(err)
      },
    })
  }

  private refreshQueryableDataSources() {
    forkJoin([
      this.emmaHttp.getMeteringPointTree(),
      this.emmaHttp.getUnassignedMetrics(),
      this.emmaHttp.getCalculations(),
      this.emmaHttp.getDeviceMappings(),
    ]).subscribe({
      next: (res) => {
        this._queryableDataSources$.next({
          tree: res[0],
          inbox: res[1],
          sums: res[2].filter((c) => c.calculationType === 'SUM'),
          avgs: res[2].filter((c) => c.calculationType === 'AVG'),
          kpis: res[2].filter((c) => c.calculationType === 'FORMULA'),
          mappings: res[3].data,
        })
        this.queryableDataSourcesInitialised = true
        this.emitStoreInitialised()
      },
      error: (err: HttpErrorResponse) => {
        this.errored = true
        this.queryableDataSourcesInitialised = true
        this.emitStoreInitialised()
      },
    })
  }

  public getUserDashboards() {
    return this.emmaHttp.getUserDashboards()
  }

  private getCosts() {
    return this.emmaHttp.getCosts().subscribe({
      next: (res) => {
        this._costs$.next(res)
        this.costsInitialised = true
        this.emitStoreInitialised()
      },
      error: (err: HttpErrorResponse) => {
        this._costs$.error(err)
        this.emitStoreInitialised()
      },
    })
  }

  public updateUserDashboards(config: DashboardConfig[]) {
    return this.emmaHttp.updateUserDashboards(config)
  }

  private loadAllUnitsAndMeasurementTypes() {
    forkJoin([this.emmaHttp.getBaseUnitDefaults(), this.emmaHttp.getWageTypeInfo(), this.emmaHttp.getUnitTypes(), this.emmaHttp.getBaseUnits()]).subscribe({
      next: (res) => {
        this._allTypesAndUnits$.next({
          baseUnitTypeDefaults: res[0],
          wageTypeInfo: res[1],
          baseTypes: res[2].baseTypes,
          compositeTypes: res[2].compositeTypes,
          baseUnits: res[3],
        })
        this.allTypesAndUnitsInitialised = true
        this.emitStoreInitialised()
      },
      error: (err) => {
        this.errored = true
        this._allTypesAndUnits$.error(err)
        this.emitStoreInitialised()
      },
    })
  }

  public loadAlertMessagesAndRules() {
    forkJoin([this.emmaHttp.getAlertRules(), this.emmaHttp.getEmmaAlertMessages()]).subscribe({
      next: (res) => {
        this._alertMessagesAndRules$.next({
          rules: res[0].data,
          messages: res[1].data,
        })

        this.alertsInitialised = true
        this.emitStoreInitialised()
      },
      error: (err) => {
        this.errored = true
        this._alertMessagesAndRules$.error(err)
        this.emitStoreInitialised()
      },
    })
  }

  public getEmmaAlertRules() {
    return this.emmaHttp.getAlertRules().subscribe({
      next: (res) => {
        this._alertRules$.next(res.data)
        this.alertsInitialised = true
        this.emitStoreInitialised()
      },
      error: (err: HttpErrorResponse) => {
        this.errored = true
        this._alertRules$.error(err)
        this.alertsInitialised = true
        this.emitStoreInitialised()
      },
    })
  }

  public getEmmaAlertMessages(options: EmmaAlertMessagesOptions = { status: 'all', page: 1, limit: 25 }) {
    return this.emmaHttp.getEmmaAlertMessages(options).subscribe({
      next: (res) => {
        this._alertMessages$.next(res.data)
      },
      error: (err: HttpErrorResponse) => {
        // this.errored = true
        this._alertMessages$.error(err)
      },
    })
  }

  public getAlertRuleMessages(parentID: string, options: EmmaAlertMessagesOptions = { status: 'all', page: 1, limit: 25 }) {
    return this.emmaHttp.getAlertRuleMessages(parentID, options).subscribe({
      next: (res) => {
        this._alertMessages$.next(res.data)
      },
      error: (err: HttpErrorResponse) => {
        // this.errored = true
        this._alertMessages$.error(err)
      },
    })
  }

  public getEmmaAlertMessage(id: string) {
    return this.emmaHttp.getEmmaAlertMessage(id)
  }

  public deleteOrphanAlertMessages() {
    return this.wrapSuccessWithAlertRulesRefresh(this.emmaHttp.deleteOrphanAlertMessages())
  }

  private setupEmmaAlerts() {
    // Do nothing if we don't have any EMMA subscriptions, this prevents a stream being opened which then gets cut off after 45 seconds due to inactivity
    if (!this.billing.emmaSubscriptions?.length) {
      return
    }
    // Set up SSE stream for alerts
    this._alertMessageStreamSubscription = this.emmaHttp.observeEmmaAlerts().subscribe({
      next: (alert: EmmaAlertMessage) => {
        if (alert?.metricId) {
          // Default expansion state (maybe move to service)
          alert.expansionState = 'collapsed'
          alert.responsible = 'emma'
          this._alertMessages$.next(alert)
        }
      },
      error: (err) => {
        console.log('Alerts stream error', err)
      },
    })
  }

  // Wrappers for POST/PUT/PATCH/DELETE requests
  private wrapSuccessWithQueryableRefresh<T>(apiCall$: Observable<T>) {
    return new Observable<T>((res) => {
      apiCall$.subscribe({
        next: (response) => {
          this.refreshQueryableDataSources()
          res.next(response)
        },
        error: (err) => {
          res.error(err)
        },
      })
    })
  }

  private wrapSuccessWithUnitsRefresh<T>(apiCall$: Observable<T>) {
    return new Observable<T>((res) => {
      apiCall$.subscribe({
        next: (response) => {
          this.loadAllUnitsAndMeasurementTypes()
          res.next(response)
        },
        error: (err) => {
          res.error(err)
        },
      })
    })
  }

  private wrapSuccessWithUnitsAndQueryablesRefresh<T>(apiCall$: Observable<T>) {
    return new Observable<T>((res) => {
      apiCall$.subscribe({
        next: (response) => {
          this.loadAllUnitsAndMeasurementTypes()
          this.refreshQueryableDataSources()
          res.next(response)
        },
        error: (err) => {
          res.error(err)
        },
      })
    })
  }

  private wrapSuccessWithAlertRulesRefresh<T>(apiCall$: Observable<T>) {
    return new Observable<T>((res) => {
      apiCall$.subscribe({
        next: (response) => {
          this.getEmmaAlertRules()
          res.next(response)
        },
        error: (err) => {
          res.error(err)
        },
      })
    })
  }

  private wrapWithDataFetchErrorFlag<T>(apiCall$: Observable<T>) {
    return new Observable<T>((res) => {
      apiCall$.subscribe({
        next: (response) => {
          res.next(response)
          res.complete()
        },
        error: (err) => {
          this.dataFetchError = true
          res.error(err)
        },
      })
    })
  }

  public createMeteringPoint(toCreate: MeteringPoint) {
    return this.wrapSuccessWithQueryableRefresh(this.emmaHttp.createMeteringPoint(toCreate))
  }

  public updateMeteringPoint(id: string, params: Partial<MeteringPoint>) {
    return this.emmaHttp.updateMeteringPoint(id, params)
  }

  public updateMeteringPointWithRefresh(id: string, params: Partial<MeteringPoint>) {
    return this.wrapSuccessWithQueryableRefresh(this.emmaHttp.updateMeteringPoint(id, params))
  }

  public deleteMeteringPoint(id: string) {
    return this.wrapSuccessWithQueryableRefresh(this.emmaHttp.deleteMeteringPoint(id))
  }

  public deleteMetric(id: string) {
    return this.wrapSuccessWithQueryableRefresh(this.emmaHttp.deleteMetric(id))
  }

  public editMetric(id: string, params: Partial<Metric>) {
    return this.wrapSuccessWithUnitsRefresh(this.wrapSuccessWithQueryableRefresh(this.emmaHttp.editMetric(id, params)))
  }

  public assignMetricToMeteringPoint(metricId: string, meteringPointId: string | null) {
    return this.wrapSuccessWithQueryableRefresh(this.emmaHttp.assignMetricToMeteringPoint(metricId, meteringPointId))
  }

  public editAggregatedMetric(id: string, params: Partial<AggregatedMetric>) {
    return this.emmaHttp.editAggregatedMetric(id, params)
  }

  public deleteCalculation(id: string) {
    return this.wrapSuccessWithUnitsAndQueryablesRefresh(this.emmaHttp.deleteCalculation(id))
  }

  public updateCalculation(id: string, params: { appearance: { colour?: string } }) {
    return this.wrapSuccessWithUnitsAndQueryablesRefresh(this.emmaHttp.updateCalculation(id, params))
  }

  public createSumCalculation(params: { name: string; metricIds: string[]; meteringPointIds: TypedQueryableID[] }) {
    return this.wrapSuccessWithUnitsAndQueryablesRefresh(this.emmaHttp.createSumCalculation(params))
  }

  public editSumCalculation(id: string, params: { name: string; metricIds: string[]; meteringPointIds: TypedQueryableID[] }) {
    return this.wrapSuccessWithUnitsAndQueryablesRefresh(this.emmaHttp.editSumCalculation(id, params))
  }

  public createAverageCalculation(params: { name: string; metricIds: string[]; meteringPointIds: TypedQueryableID[] }) {
    return this.wrapSuccessWithUnitsAndQueryablesRefresh(this.emmaHttp.createAverageCalculation(params))
  }

  public editAverageCalculation(id: string, params: { name: string; metricIds: string[]; meteringPointIds: TypedQueryableID[] }) {
    return this.wrapSuccessWithUnitsAndQueryablesRefresh(this.emmaHttp.editAverageCalculation(id, params))
  }

  public createRate(costId: string, newRate: Rate) {
    return this.emmaHttp.createRate(costId, newRate)
  }

  public updateRate(costId: string, newRate: Rate) {
    return this.emmaHttp.updateRate(costId, newRate)
  }

  public deleteRate(costId: string, newRate: Rate) {
    return this.emmaHttp.deleteRate(costId, newRate)
  }

  public updateCost(costId: string, price: number) {
    return this.emmaHttp.updateCost(costId, price)
  }

  public updateCostUnit(costId: string, unit: Unit, type: 'cost' | 'consumption') {
    return this.emmaHttp.updateCostUnit(costId, unit, type)
  }

  public createFormulaCalculation(
    name: string,
    nodes: {
      type: string
      value: string | { prefix: UnitPrefix; baseUnit: string } | { id: string; unitType: BaseUnitTypeID; measurementType: AcceptableMeasurementTypes }
    }[]
  ) {
    return this.wrapSuccessWithUnitsAndQueryablesRefresh(this.emmaHttp.createFormulaCalculation(name, nodes))
  }

  public updateFormulaCalculation(
    id: string,
    name: string,
    nodes: {
      type: string
      value: string | { prefix: UnitPrefix; baseUnit: string } | { id: string; unitType: BaseUnitTypeID; measurementType: AcceptableMeasurementTypes }
    }[]
  ) {
    return this.wrapSuccessWithUnitsAndQueryablesRefresh(this.emmaHttp.updateFormulaCalculation(id, name, nodes))
  }

  public validateFormula(nodes: { type: FormulaNodeTypes; value: any }[]) {
    return this.emmaHttp.validateFormula(nodes)
  }

  public uploadCSVMetrics(filesData: Record<string, string>[], meteringPointId?: string) {
    return this.wrapSuccessWithQueryableRefresh(this.emmaHttp.uploadCSVMetrics(filesData, meteringPointId))
  }

  public acceptCSVOverwrite(taskId: string) {
    return this.emmaHttp.acceptCSVOverwrite(taskId)
  }

  public getAvailbleDMSDevices() {
    return this.emmaHttp.getAvailbleDMSDevices()
  }

  public createDMSDeviceMapping(mappings: CreateDeviceMapping[]) {
    return this.wrapSuccessWithUnitsRefresh(this.wrapSuccessWithQueryableRefresh(this.emmaHttp.createDMSDeviceMapping(mappings)))
  }

  public replaceDMSDeviceMapping(mapping: ReplaceDeviceMapping) {
    return this.wrapSuccessWithUnitsRefresh(this.wrapSuccessWithQueryableRefresh(this.emmaHttp.replaceDMSDeviceMapping(mapping)))
  }

  public createAlertRule(newAlertRule: AlertRule) {
    return this.wrapSuccessWithAlertRulesRefresh(this.emmaHttp.createAlertRule(newAlertRule))
  }

  public updateAlertRule(id: string, params: Partial<AlertRule>) {
    return this.emmaHttp.updateAlertRule(id, params)
  }

  public deleteAlertRule(alertRuleID: string) {
    return this.wrapSuccessWithAlertRulesRefresh(this.emmaHttp.deleteAlertRule(alertRuleID))
  }

  // Query data methods
  public queryWidgetData(
    startDates: Date[],
    endDates: Date[],
    queryables: QueryableTypedID[],
    groupBy: GroupByUnits,
    options?: {
      representation?: DataRepresentations
      unit?: BaseUnitID
      unitType?: BaseUnitTypeID | 'kpi'
      prefix?: UnitPrefix
      aggregation?: AggregationMethods
      costType?: CostsTypes
    }
  ) {
    return this.wrapWithDataFetchErrorFlag(this.emmaHttp.queryWidgetData(startDates, endDates, queryables, groupBy, options))
  }

  public queryWidgetMetaData(startDates: Date[], endDates: Date[], queryables: QueryableTypedID[], representation?: DataRepresentations) {
    return this.wrapWithDataFetchErrorFlag(this.emmaHttp.queryWidgetMetaData(startDates, endDates, queryables, { representation }))
  }

  public queryWidgetDataAggregated(
    startDates: Date[],
    endDates: Date[],
    queryables: QueryableTypedID[],
    groupBy?: GroupByUnits,
    options?: {
      representation?: DataRepresentations
      unit?: BaseUnitID
      unitType?: BaseUnitTypeID | 'kpi'
      prefix?: UnitPrefix
      aggregation?: AggregationMethods
      costType?: CostsTypes
    }
  ) {
    return this.wrapWithDataFetchErrorFlag(this.emmaHttp.queryWidgetDataAggregated(startDates, endDates, queryables, groupBy, options))
  }

  public queryWidgetDataCSV(
    startDates: Date[],
    endDates: Date[],
    queryables: QueryableTypedID[],
    groupBy: GroupByUnits,
    unitType?: BaseUnitTypeID | 'kpi',
    representation?: DataRepresentations
  ) {
    return this.wrapWithDataFetchErrorFlag(this.emmaHttp.queryWidgetDataCSV(startDates, endDates, queryables, groupBy, { unitType, representation }))
  }

  public querySummedData(startDates: Date[], endDates: Date[], queryables: QueryableTypedID[], groupBy: GroupByUnits) {
    return this.wrapWithDataFetchErrorFlag(this.emmaHttp.querySummedData(startDates, endDates, queryables, groupBy))
  }

  public requestDateEmailExport(
    emails: string[],
    startDates: Date[],
    endDates: Date[],
    queryableIds: QueryableTypedID[],
    groupBy: GroupByUnits,
    unitType?: BaseUnitTypeID | 'kpi',
    representation?: DataRepresentations
  ) {
    return this.emmaHttp.requestDateEmailExport(emails, startDates, endDates, queryableIds, groupBy, {
      unitType,
      representation,
    })
  }

  public queryWidgetStatistics(
    startDates: Date[],
    endDates: Date[],
    queryables: QueryableTypedID[],
    groupBy: GroupByUnits,
    unitType?: BaseUnitTypeID | 'kpi',
    representation?: DataRepresentations,
    options?: {
      unit?: BaseUnitID
      unitType?: BaseUnitTypeID
      prefix?: UnitPrefix
    }
  ) {
    return this.wrapWithDataFetchErrorFlag(this.emmaHttp.queryWidgetStatistics(startDates, endDates, queryables, groupBy, unitType, representation, options))
  }

  public queryRawData(metricIds: string[], startDate: Date, endDate: Date, options?: { page?: number; limit?: number; includeEmpty?: boolean }) {
    return this.wrapWithDataFetchErrorFlag(this.emmaHttp.queryRawData(metricIds, startDate, endDate, options))
  }

  public getChangeLogs(metricIds: string[], startDate: Date, endDate: Date, options?: { page?: number; limit?: number }) {
    return this.wrapWithDataFetchErrorFlag(this.emmaHttp.getChangeLogs(metricIds, startDate, endDate, options))
  }

  public updateMetricValue(id: string, timestamp: string, fromValue: string | null, toValue: string | null) {
    return this.emmaHttp.updateMetricValue(id, timestamp, fromValue, toValue).pipe(
      tap((resp) => {
        this._rawDataChanged$.next({ metridId: id })
        return resp
      })
    )
  }

  public revertChangeEvent(ce: ChangeEvent) {
    return this.emmaHttp.revertChangeEvent(ce.id).pipe(
      tap((resp) => {
        this._rawDataChanged$.next({ metridId: ce.metricId })
        return resp
      })
    )
  }
}
